/**
 * Date Utilities
 * Helper functions for date formatting and manipulation
 */

/**
 * Format date to YYYY-MM-DD
 * @param {Date|string} date - Date to format
 * @returns {string} Formatted date string
 */
export function formatDate(date) {
  const d = date instanceof Date ? date : new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Format date to readable string (e.g., "Wednesday, November 12, 2025")
 * @param {Date|string} date - Date to format
 * @returns {string} Formatted date string
 */
export function formatDateReadable(date) {
  const d = date instanceof Date ? date : new Date(date);
  const options = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
  return d.toLocaleDateString('en-US', options);
}

/**
 * Format time to HH:MM AM/PM
 * @param {string} timeString - Time string (e.g., "07:00:00")
 * @returns {string} Formatted time string
 */
export function formatTime(timeString) {
  if (!timeString) return '';
  
  const [hours, minutes] = timeString.split(':');
  const hour = parseInt(hours, 10);
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const hour12 = hour % 12 || 12;
  return `${hour12}:${minutes} ${ampm}`;
}

/**
 * Format time range (e.g., "7:00 AM - 8:30 AM")
 * @param {string} startTime - Start time string
 * @param {string} endTime - End time string
 * @returns {string} Formatted time range
 */
export function formatTimeRange(startTime, endTime) {
  return `${formatTime(startTime)} - ${formatTime(endTime)}`;
}

/**
 * Get start of week (Saturday) for a given date
 * Islamic week format: Saturday to Friday
 * @param {Date|string} date - Date to get week start for
 * @returns {Date} Start of week (Saturday)
 */
export function getWeekStart(date) {
  const d = date instanceof Date ? new Date(date) : new Date(date);
  const day = d.getDay(); // 0 = Sunday, 6 = Saturday
  
  // Calculate days to subtract to get to Saturday
  // If day is 0 (Sunday), subtract 1 to get Saturday
  // If day is 6 (Saturday), subtract 0
  // Otherwise, subtract (day + 1) to get to previous Saturday
  const daysToSubtract = day === 0 ? 1 : day === 6 ? 0 : day + 1;
  
  const weekStart = new Date(d);
  weekStart.setDate(d.getDate() - daysToSubtract);
  weekStart.setHours(0, 0, 0, 0);
  
  return weekStart;
}

/**
 * Get end of week (Friday) for a given date
 * @param {Date|string} date - Date to get week end for
 * @returns {Date} End of week (Friday)
 */
export function getWeekEnd(date) {
  const weekStart = getWeekStart(date);
  const weekEnd = new Date(weekStart);
  weekEnd.setDate(weekStart.getDate() + 6); // Add 6 days to get Friday
  weekEnd.setHours(23, 59, 59, 999);
  return weekEnd;
}

/**
 * Get all days in week (Saturday to Friday)
 * @param {Date|string} date - Date within the week
 * @returns {Date[]} Array of dates for the week
 */
export function getWeekDays(date) {
  const weekStart = getWeekStart(date);
  const days = [];
  
  for (let i = 0; i < 7; i++) {
    const day = new Date(weekStart);
    day.setDate(weekStart.getDate() + i);
    days.push(day);
  }
  
  return days;
}

/**
 * Check if date is today
 * @param {Date|string} date - Date to check
 * @returns {boolean}
 */
export function isToday(date) {
  const d = date instanceof Date ? date : new Date(date);
  const today = new Date();
  return (
    d.getDate() === today.getDate() &&
    d.getMonth() === today.getMonth() &&
    d.getFullYear() === today.getFullYear()
  );
}

/**
 * Get day name abbreviation
 * @param {Date|string} date - Date to get day name for
 * @returns {string} Day abbreviation (Sat, Sun, Mon, etc.)
 */
export function getDayAbbr(date) {
  const d = date instanceof Date ? date : new Date(date);
  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  return days[d.getDay()];
}

/**
 * Get day name full
 * @param {Date|string} date - Date to get day name for
 * @returns {string} Full day name
 */
export function getDayName(date) {
  const d = date instanceof Date ? date : new Date(date);
  const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  return days[d.getDay()];
}




